import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { loginSchema, signupSchema, type LoginInput, type SignupInput } from "@/lib/validations/auth";
import { AlertCircle, Loader2, Eye, EyeOff } from "lucide-react";
import { calculatePasswordStrength, type PasswordStrength } from "@/lib/utils/passwordStrength";
import { Separator } from "@/components/ui/separator";

interface AuthFormProps {
  mode: "login" | "signup";
}

export const AuthForm = ({ mode }: AuthFormProps) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState<PasswordStrength | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const { toast } = useToast();
  const navigate = useNavigate();

  const handlePasswordChange = (value: string) => {
    setPassword(value);
    if (mode === "signup" && value) {
      const result = calculatePasswordStrength(value);
      setPasswordStrength(result.strength);
    }
  };

  const getErrorMessage = (error: any): string => {
    const errorMsg = error?.message || "";
    
    if (errorMsg.includes("Email not confirmed")) {
      return "Bitte bestätige zuerst deine E-Mail-Adresse. Überprüfe dein Postfach.";
    }
    if (errorMsg.includes("Invalid login credentials")) {
      return "E-Mail oder Passwort ist falsch.";
    }
    if (errorMsg.includes("User already registered")) {
      return "Ein Konto mit dieser E-Mail-Adresse existiert bereits.";
    }
    if (errorMsg.includes("over_email_send_rate_limit")) {
      return "Zu viele E-Mails versendet. Bitte warte einen Moment.";
    }
    
    return errorMsg || "Ein unbekannter Fehler ist aufgetreten.";
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    try {
      if (mode === "login") {
        const validatedData = loginSchema.parse({ email, password });
        await handleLogin(validatedData);
      } else {
        const validatedData = signupSchema.parse({ 
          firstName, 
          lastName, 
          email, 
          password, 
          acceptTerms 
        });
        await handleSignup(validatedData);
      }
    } catch (error: any) {
      if (error.errors) {
        const fieldErrors: Record<string, string> = {};
        error.errors.forEach((err: any) => {
          fieldErrors[err.path[0]] = err.message;
        });
        setErrors(fieldErrors);
      }
    }
  };

  const handleLogin = async (data: LoginInput) => {
    setIsLoading(true);

    try {
      const { error } = await supabase.auth.signInWithPassword({
        email: data.email,
        password: data.password,
      });

      if (error) {
        if (error.message.includes("Email not confirmed")) {
          navigate(`/email-confirmation?email=${encodeURIComponent(data.email)}`);
          return;
        }
        throw error;
      }

      // Handle "Remember Me" functionality
      if (!rememberMe) {
        const storageKey = `sb-sbamevzihcromyafxyrw-auth-token`;
        const sessionData = localStorage.getItem(storageKey);
        if (sessionData) {
          sessionStorage.setItem(storageKey, sessionData);
          localStorage.removeItem(storageKey);
        }
      }

      toast({
        title: "Erfolgreich angemeldet",
        description: "Du wirst weitergeleitet...",
      });

      navigate("/");
    } catch (error: any) {
      toast({
        title: "Anmeldung fehlgeschlagen",
        description: getErrorMessage(error),
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignup = async (data: SignupInput) => {
    setIsLoading(true);

    try {
      const { error } = await supabase.auth.signUp({
        email: data.email,
        password: data.password,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/callback`,
          data: {
            first_name: data.firstName,
            last_name: data.lastName,
          },
        },
      });

      if (error) throw error;

      // Redirect to email confirmation page
      navigate(`/email-confirmation?email=${encodeURIComponent(data.email)}`);
    } catch (error: any) {
      toast({
        title: "Registrierung fehlgeschlagen",
        description: getErrorMessage(error),
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setIsLoading(true);
    
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
        },
      });

      if (error) throw error;
    } catch (error: any) {
      toast({
        title: "Google-Anmeldung fehlgeschlagen",
        description: getErrorMessage(error),
        variant: "destructive",
      });
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-5">
      {/* Google Auth Button */}
      <Button
        type="button"
        variant="outline"
        className="w-full h-11 text-sm font-medium bg-background/50 border-border/60 hover:bg-background hover:border-border transition-all duration-200"
        onClick={handleGoogleLogin}
        disabled={isLoading}
      >
        <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
          <path
            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
            fill="#4285F4"
          />
          <path
            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            fill="#34A853"
          />
          <path
            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
            fill="#FBBC05"
          />
          <path
            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
            fill="#EA4335"
          />
        </svg>
        {mode === "login" ? "Mit Google anmelden" : "Mit Google registrieren"}
      </Button>

      {/* Divider */}
      <div className="relative my-5">
        <Separator className="bg-border/40" />
        <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-card px-3 text-xs text-muted-foreground/70">
          oder mit E-Mail
        </span>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-4">
        {mode === "signup" && (
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="firstName" className="text-sm">Vorname</Label>
              <Input
                id="firstName"
                type="text"
                placeholder="Max"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                disabled={isLoading}
                className={`h-11 bg-background/50 border-border/60 focus:border-primary/50 focus:ring-primary/20 transition-all ${
                  errors.firstName ? "border-destructive" : ""
                }`}
              />
              {errors.firstName && (
                <p className="text-xs text-destructive flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  {errors.firstName}
                </p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="lastName" className="text-sm">Nachname</Label>
              <Input
                id="lastName"
                type="text"
                placeholder="Mustermann"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                disabled={isLoading}
                className={`h-11 bg-background/50 border-border/60 focus:border-primary/50 focus:ring-primary/20 transition-all ${
                  errors.lastName ? "border-destructive" : ""
                }`}
              />
              {errors.lastName && (
                <p className="text-xs text-destructive flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  {errors.lastName}
                </p>
              )}
            </div>
          </div>
        )}

        <div className="space-y-1.5">
          <Label htmlFor="email" className="text-sm">E-Mail-Adresse</Label>
          <Input
            id="email"
            type="email"
            placeholder="name@beispiel.de"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={isLoading}
            className={`h-11 bg-background/50 border-border/60 focus:border-primary/50 focus:ring-primary/20 transition-all ${
              errors.email ? "border-destructive" : ""
            }`}
          />
          {errors.email && (
            <p className="text-xs text-destructive flex items-center gap-1">
              <AlertCircle className="h-3 w-3" />
              {errors.email}
            </p>
          )}
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="password" className="text-sm">Passwort</Label>
          <div className="relative">
            <Input
              id="password"
              type={showPassword ? "text" : "password"}
              placeholder={mode === "signup" ? "Mindestens 8 Zeichen" : "••••••••"}
              value={password}
              onChange={(e) => handlePasswordChange(e.target.value)}
              disabled={isLoading}
              className={`h-11 pr-10 bg-background/50 border-border/60 focus:border-primary/50 focus:ring-primary/20 transition-all ${
                errors.password ? "border-destructive" : ""
              }`}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground/60 hover:text-muted-foreground transition-colors"
              disabled={isLoading}
            >
              {showPassword ? (
                <EyeOff className="h-4 w-4" />
              ) : (
                <Eye className="h-4 w-4" />
              )}
            </button>
          </div>
          
          {mode === "signup" && password && passwordStrength && (
            <div className="space-y-1.5 pt-1">
              <div className="flex gap-1">
                <div 
                  className={`h-1 flex-1 rounded-full transition-colors ${
                    passwordStrength === "weak" 
                      ? "bg-destructive" 
                      : "bg-muted/50"
                  }`} 
                />
                <div 
                  className={`h-1 flex-1 rounded-full transition-colors ${
                    passwordStrength === "medium" || passwordStrength === "strong"
                      ? passwordStrength === "medium" ? "bg-orange-500" : "bg-muted/50"
                      : "bg-muted/50"
                  }`} 
                />
                <div 
                  className={`h-1 flex-1 rounded-full transition-colors ${
                    passwordStrength === "strong" 
                      ? "bg-green-500" 
                      : "bg-muted/50"
                  }`} 
                />
              </div>
              <p className={`text-xs ${
                passwordStrength === "weak" 
                  ? "text-destructive" 
                  : passwordStrength === "medium" 
                  ? "text-orange-500" 
                  : "text-green-600"
              }`}>
                {calculatePasswordStrength(password).feedback}
              </p>
            </div>
          )}
          
          {mode === "signup" && !password && (
            <p className="text-xs text-muted-foreground/70 pt-0.5">
              Mindestens 8 Zeichen für optimale Sicherheit
            </p>
          )}
          
          {errors.password && (
            <p className="text-xs text-destructive flex items-center gap-1">
              <AlertCircle className="h-3 w-3" />
              {errors.password}
            </p>
          )}
        </div>

        {mode === "login" && (
          <div className="flex items-center justify-between pt-1">
            <div className="flex items-center gap-2">
              <Checkbox
                id="rememberMe"
                checked={rememberMe}
                onCheckedChange={(checked) => setRememberMe(checked === true)}
                disabled={isLoading}
                className="border-border/60 data-[state=checked]:bg-primary data-[state=checked]:border-primary"
              />
              <Label 
                htmlFor="rememberMe" 
                className="text-sm cursor-pointer font-normal text-muted-foreground"
              >
                Angemeldet bleiben
              </Label>
            </div>
          </div>
        )}

        {mode === "signup" && (
          <div className="space-y-2 pt-1">
            <div className="flex items-start gap-2">
              <Checkbox
                id="terms"
                checked={acceptTerms}
                onCheckedChange={(checked) => setAcceptTerms(checked === true)}
                disabled={isLoading}
                className="mt-0.5 border-border/60 data-[state=checked]:bg-primary data-[state=checked]:border-primary"
              />
              <Label
                htmlFor="terms"
                className="text-sm leading-relaxed cursor-pointer font-normal text-muted-foreground"
              >
                Ich akzeptiere die{" "}
                <Link
                  to="/legal/terms"
                  target="_blank"
                  className="text-primary hover:underline underline-offset-2"
                >
                  Nutzungsbedingungen
                </Link>{" "}
                und{" "}
                <Link
                  to="/legal/privacy"
                  target="_blank"
                  className="text-primary hover:underline underline-offset-2"
                >
                  Datenschutzerklärung
                </Link>
              </Label>
            </div>
            {errors.acceptTerms && (
              <p className="text-xs text-destructive flex items-center gap-1">
                <AlertCircle className="h-3 w-3" />
                {errors.acceptTerms}
              </p>
            )}
          </div>
        )}

        <Button
          type="submit"
          className="w-full h-11 text-sm font-medium mt-2 shadow-lg shadow-primary/20 hover:shadow-xl hover:shadow-primary/30 transition-all duration-300"
          disabled={isLoading}
        >
          {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {isLoading
            ? "Wird verarbeitet..."
            : mode === "login"
            ? "Anmelden"
            : "Konto erstellen"}
        </Button>
      </form>
    </div>
  );
};
